/* -----------------Globals--------------- */
import { useState } from "react";

import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------UI--------------- */
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";

/* -----------------Types--------------- */
import { events } from "@constants/tracking";

import { flattenSessionItems } from "@features/session/helpers/session";
import { useSession } from "@features/session/hooks/useSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import i18n from "@helpers/lib/i18n";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import useAuthUser from "@hooks/useAuthUser";
import { titleCase } from "@memorang/helpers/common";
import { useToast } from "@memorang/ui/components/Toast";
import { trackCustomEvent } from "analytics";
import { getMixedItemDistribution } from "api/queries";
import { router } from "expo-router";
import CreateSessionLoader from "./CreateSessionLoader";

type Props = {
	visible: boolean;
	onClose: (navigate?: boolean) => void;
};
const FreeQuizSessionDialog = ({ visible, onClose }: Props) => {
	const [creatingSession, setCreatingSession] = useState(false);
	const {
		currentExamContext: { examName, examId },
	} = useExamContext();
	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const cta = diagnosticLabel
		? `Start ${diagnosticLabel[i18n.language]}`
		: "Start diagnostic";

	const title = diagnosticLabel
		? titleCase(diagnosticLabel[i18n.language])
		: "Diagnostic Assessment";
	const subtitle = `Subject: ${examName} • 20 Items`;
	const body = `Not sure where to start studying? Take our ${
		diagnosticLabel
			? `**${diagnosticLabel[i18n.language]}**`
			: "free **20-item Diagnostic Assessment**"
	}. It's a great way to familiarize yourself with the format and content of the exam. At the end of your diagnostic assessment we will provide you with a detailed breakdown of your strengths/weaknesses, rationales for each item, and tips for what you can do between now and your exam date to better prepare.  
    Your total time allotted is **30 mins** which comes out to about **72 seconds** per question.
    `;

	const updateSession = useSessionStore((state) => state.updateSession);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const { createNewSession } = useSession();
	const { showToast } = useToast();
	const {
		currentExamContext: {
			projectId,
			organizationId,
			practiceTestProductNumericId,
		},
	} = useExamContext();
	const { userPublicMetadataId } = useAuthUser();
	const handleCreateSession = async (collectionId: number) => {
		try {
			resetSessionStore();
			trackCustomEvent({
				eventName: events.freeQuizStarted,
				type: "summative",
			});
			const params = {
				collectionId,
				projectId,
				organizationId,
				sessionType: "SUMMATIVE" as const,
				sessionSubType: "DIAGNOSTIC" as const,
			} as const;
			const result = await createNewSession(params);
			if (!result.sessionItems?.length) {
				showToast({
					message: "Failed to create session, try again",
				});
				const error = new Error(
					"Error creating diagnostic session: session list is empty",
				);
				captureErrorToSentry(error, {
					error: serializeErrorForSentry(result),
				});
				return;
			}
			const flattenedSessionItems = flattenSessionItems(result.sessionItems);

			const newSessionResponse = {
				...result,
				sessionItems: flattenedSessionItems,
			};
			updateSession({
				sessionResponse: newSessionResponse,
				isSummativeTest: true,
				isDiagnostic: false,
			});

			onClose();

			router.replace({
				pathname: "/(protected)/session/[id]",
				params: {
					id: result.numericId,
					index: "1",
				},
			});
		} catch (error) {
			showToast({
				message: "Failed to create session, try again",
			});
			captureErrorToSentry(new Error("Error creating diagnostic session"), {
				error: serializeErrorForSentry(error),
			});
		} finally {
			setCreatingSession(false);
		}
	};

	const handleStartFreeQuiz = async () => {
		if (practiceTestProductNumericId) {
			setCreatingSession(true);
			try {
				const res = await getMixedItemDistribution({
					userPublicMetadataId: userPublicMetadataId!,
					examId,
					action: "GET_PRATICETEST_DISTRIBUTION",
				});
				const practiceTestQuizzes =
					res?.exams?.filter((exam) => exam.subType === "DIAGNOSTIC") ?? [];
				const diagnosticQuiz = practiceTestQuizzes[0];
				if (!diagnosticQuiz?.collectionId) {
					showToast({
						message: "Failed to create session, try again",
					});
					setCreatingSession(false);
					return;
				}
				await handleCreateSession(diagnosticQuiz.collectionId);
			} catch (error) {
				showToast({
					message: "Failed to create session, try again",
				});
				captureErrorToSentry(
					new Error("Error fetching diagnostic distribution"),
					{
						error: serializeErrorForSentry(error),
						examId,
					},
				);
				setCreatingSession(false);
			}
		}
	};

	if (creatingSession) {
		return <CreateSessionLoader />;
	}
	return (
		<ConfirmDialog
			visible={visible}
			title={title}
			body={body}
			subtitle={subtitle}
			icon="format-list-checks"
			dismissCallback={() => {
				onClose(true);
			}}
			dismissCta="Maybe Later"
			confirmCallback={handleStartFreeQuiz}
			confirmCta={cta}
		/>
	);
};

export default FreeQuizSessionDialog;
